/**
 * Wall Foundation Tool - BIM 2025 Grade
 * 
 * Continuous wall footings with:
 * - Bearing and Retaining types
 * - Stepped footing support
 * - Eccentricity (offset from wall center)
 * - Auto-host to walls
 * - Analytical model generation
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, RebarHostData, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Foundation Types ====================

export type WallFoundationType =
    | 'Bearing Footing'     // Centered, symmetric
    | 'Retaining Footing'   // Asymmetric, heel/toe
    | 'Combined Footing';   // Multiple walls

export type FootingProfile = 'Rectangular' | 'Tapered' | 'Stepped';

// ==================== Enhanced Geometry ====================

export interface EnhancedWallFoundationGeometryData {
    // Type
    foundationType: WallFoundationType;
    profile: FootingProfile;

    // Path
    start: [number, number, number];
    end: [number, number, number];
    length: number;             // m

    // Dimensions
    width: number;              // mm
    thickness: number;          // mm
    heelWidth?: number;         // mm (for retaining)
    toeWidth?: number;          // mm (for retaining)

    // Positioning
    eccentricity: number;       // mm (offset from wall centerline)
    levelId: string;
    heightOffset: number;       // mm

    // Stepped footing data
    isStepped: boolean;
    stepHeight?: number;        // mm

    // Calculated
    volume: number;             // m³
    weight: number;             // kg
    bearingArea: number;        // m²
}

// ==================== Enhanced Wall Foundation Element ====================

export interface EnhancedWallFoundationElement extends Element {
    category: 'Structural Foundations';
    geometryData: EnhancedWallFoundationGeometryData;

    // Structural
    structuralMaterial: StructuralMaterial;
    rebarHostData: RebarHostData;

    // Host
    hostWallId?: string;

    // Foundation-specific data (not using analyticalModel to avoid type mismatch)
    foundationCurve?: [[number, number, number], [number, number, number]];
    supportType?: 'Continuous' | 'Isolated';
}

// ==================== Tool Options ====================

export interface WallFoundationToolOptions {
    // Type
    foundationType: WallFoundationType;
    familyName: string;
    typeName: string;

    // Dimensions
    width: number;              // mm
    thickness: number;          // mm
    heelWidth: number;          // mm
    toeWidth: number;           // mm

    // Positioning
    eccentricity: number;       // mm
    levelId: string;
    heightOffset: number;

    // Material
    materialId: string;

    // Behavior
    autoHost: boolean;          // Snap to walls
    breakAtInserts: boolean;    // Break at doors/windows (optional)
}

// ==================== Wall Foundation Tool Class ====================

export class WallFoundationTool extends BaseTool {
    id = 'wall_foundation';
    name = 'Wall Foundation';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private hoveredWallId: string | null = null;

    private options: WallFoundationToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): WallFoundationToolOptions {
        return {
            foundationType: 'Bearing Footing',
            familyName: 'Wall Foundation',
            typeName: '900 x 300mm',
            width: 900,
            thickness: 300,
            heelWidth: 300,
            toeWidth: 300,
            eccentricity: 0,
            levelId: 'Level 1',
            heightOffset: 0,
            materialId: 'mat-concrete-30',
            autoHost: true,
            breakAtInserts: false
        };
    }

    /**
     * Set options from UI
     */
    setOptions(options: Partial<WallFoundationToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): WallFoundationToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Check for wall host
        if (this.options.autoHost) {
            const wallHit = this.getElementIntersection(event, 'Walls');
            if (wallHit) {
                this.createFoundationUnderWall(wallHit.element);
                return;
            }
        }

        // Manual placement
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            this.createFoundation(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            // Highlight wall if hovering
            if (this.options.autoHost) {
                const wallHit = this.getElementIntersection(event, 'Walls');
                if (wallHit && wallHit.element.id !== this.hoveredWallId) {
                    this.hoveredWallId = wallHit.element.id;
                    // TODO: Highlight wall
                } else if (!wallHit) {
                    this.hoveredWallId = null;
                }
            }

            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.startPoint = null;
            this.currentPoint = null;
            this.hoveredWallId = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'foundation_wall',
                start: this.startPoint,
                end: this.currentPoint,
                width: this.options.width / 1000,
                thickness: this.options.thickness / 1000
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createFoundationUnderWall(wall: Element) {
        if (!this.context) return;

        // Extract wall geometry (simplified)
        const wallGeom = wall.geometryData as any;
        if (!wallGeom.start || !wallGeom.end) return;

        const start = new THREE.Vector3(...wallGeom.start);
        const end = new THREE.Vector3(...wallGeom.end);

        // Adjust for wall base offset/level
        // In a real implementation, we'd query the wall's base constraint

        const foundation = this.createFoundationElement(start, end, wall.id);
        this.context.bimStore.addElement(foundation);
    }

    private createFoundation(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;
        const foundation = this.createFoundationElement(start, end);
        this.context.bimStore.addElement(foundation);
    }

    private createFoundationElement(start: THREE.Vector3, end: THREE.Vector3, hostWallId?: string): EnhancedWallFoundationElement {
        const length = start.distanceTo(end);

        // Calculate volume
        const area = (this.options.width / 1000) * (this.options.thickness / 1000);
        const volume = area * length;

        const material = this.getMaterial(this.options.materialId);
        const weight = volume * material.properties.density;

        // Apply eccentricity
        // Vector perpendicular to path
        const dir = end.clone().sub(start).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0);
        const offsetVector = perp.multiplyScalar(this.options.eccentricity / 1000);

        const finalStart = start.clone().add(offsetVector);
        const finalEnd = end.clone().add(offsetVector);

        const geometryData: EnhancedWallFoundationGeometryData = {
            foundationType: this.options.foundationType,
            profile: 'Rectangular',
            start: finalStart.toArray() as [number, number, number],
            end: finalEnd.toArray() as [number, number, number],
            length,
            width: this.options.width,
            thickness: this.options.thickness,
            heelWidth: this.options.foundationType === 'Retaining Footing' ? this.options.heelWidth : undefined,
            toeWidth: this.options.foundationType === 'Retaining Footing' ? this.options.toeWidth : undefined,
            eccentricity: this.options.eccentricity,
            levelId: this.options.levelId,
            heightOffset: this.options.heightOffset,
            isStepped: false,
            volume,
            weight,
            bearingArea: (this.options.width / 1000) * length
        };

        const foundation: EnhancedWallFoundationElement = {
            id: uuidv4(),
            category: 'Structural Foundations',
            type: `${this.options.familyName}: ${this.options.width}x${this.options.thickness}mm`,
            parameters: this.buildParameters(geometryData),
            constraints: this.getDefaultConstraints(),
            geometryData,
            structuralMaterial: material,
            rebarHostData: {
                validHost: true,
                coverSettings: {
                    top: 50,
                    bottom: 75,
                    sides: 50
                }
            },
            hostWallId,
            foundationCurve: [geometryData.start, geometryData.end],
            supportType: 'Continuous'
        };

        return foundation;
    }

    private getMaterial(materialId: string): StructuralMaterial {
        // Simplified material lookup
        return {
            id: materialId,
            name: 'Concrete - Cast-in-Place',
            type: 'Concrete',
            properties: {
                youngsModulus: 30000,
                shearModulus: 12500,
                poissonRatio: 0.2,
                density: 2400,
                thermalExpansionCoefficient: 0.00001,
                concreteCompression: 30
            }
        };
    }

    private buildParameters(geom: EnhancedWallFoundationGeometryData): Record<string, any> {
        return {
            'Width': geom.width,
            'Thickness': geom.thickness,
            'Length': Math.round(geom.length * 1000) / 1000,
            'Eccentricity': geom.eccentricity,
            'Volume': Math.round(geom.volume * 1000) / 1000,
            'Weight': Math.round(geom.weight),
            'Bearing Area': Math.round(geom.bearingArea * 100) / 100,
            'Level': geom.levelId,
            'Height Offset': geom.heightOffset
        };
    }

    private getDefaultConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.levelId,
                data: { offset: this.options.heightOffset }
            }
        ];
    }
}



